<template>
  <BittsLoading :is-loading="isLoading">
    <div class="c-partner-mapping-tab">
      <BittsAlert
        v-if="!hasReadPermission"
        color="warning"
        message="You don't have permission to view partner mappings"
        description="Please contact your administrator for access."
      />
      <div v-else class="c-partner-mapping-content">
        <BittsAlert
          v-if="hasAutoMappedAccounts"
          color="info"
          message="Some accounts were auto-mapped by Crossbeam. Review them."
        />

        <PartnerMappingRuleCard
          :feed-id="feedId"
          :title="ruleTitle"
          :subtitle="ruleSubtitle"
          class="c-partner-mapping-tab__rule-box"
        />

        <div v-if="mappings.length > 0">
          <h2 class="c-partner-mapping-tab__title">Overview</h2>
          <p class="c-partner-mapping-tab__subtitle">
            Map each Crossbeam partner to the matching
            {{ terminology.objectName }} so overlaps can reference the correct
            Partner Account.
          </p>
        </div>

        <PartnerMappingTabActions
          v-if="mappings.length > 0"
          ref="partnerMappingTabActionsRef"
          :is-action-bar-disabled="isLoading"
          @search-applied="handleSearchApplied"
          @filters-applied="handleFiltersApplied"
        />

        <BittsTable
          v-if="filteredMappings.length > 0"
          :columns="columnDefinitions"
          :rows="filteredMappings"
          :hide-pagination="true"
          :pagination="false"
          :row-height="56"
          class="c-partner-mapping-tab__table"
          @grid-ready="handleGridReady"
        />

        <BittsEmptyState
          v-if="mappings.length === 0"
          size="large"
          title="No partners to map yet"
          :description="`You don't have any connected partners yet. Once you connect a partner, you'll be able to map them to the correct ${terminology.objectNamePlural} here.`"
          :button="{
            text: 'Partners',
            type: 'primary',
            variant: 'outline',
          }"
          :font-awesome-icon="['fak', 'partner']"
          font-awesome-icon-class="c-partner-mapping-tab__icon"
          :font-awesome-icon-style="{ height: '64px', width: '64px' }"
          @button-clicked="handleNavigateToPartners"
        />

        <BittsEmptyState
          v-else-if="filteredMappings.length === 0"
          size="large"
          title="No Results"
          description="Try different filters or search term to find your partner mapping"
          :button="{
            text: 'Clear Filters',
            type: 'primary',
            variant: 'outline',
            size: 'small',
          }"
          :font-awesome-icon="['fak', 'search']"
          font-awesome-icon-class="c-partner-mapping-tab__icon"
          :font-awesome-icon-style="{ height: '64px', width: '64px' }"
          @button-clicked="handleClearFilters"
        />
      </div>
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsEmptyState,
  BittsLoading,
  BittsTable,
} from '@crossbeam/bitts';

import { ColDef, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { captureException } from '@/errors';
import { useFeedsStore, useFlashesStore, usePartnersStore } from '@/stores';
import { PartnerCrmMapping } from '@/types/partner_crm_mappings';

import {
  getPartnerMappingRuleTitle,
  getPartnerMappingTerminology,
} from '../partnerMappingTerminology';

import PartnerMappingRuleCard from './PartnerMappingRuleCard.vue';
import PartnerMappingTabAccountCell from './PartnerMappingTabAccountCell.vue';
import PartnerMappingTabActions from './PartnerMappingTabActions.vue';
import PartnerMappingTabHeader from './PartnerMappingTabHeader.vue';
import PartnerMappingTabPartnerCell from './PartnerMappingTabPartnerCell.vue';
import PartnerMappingTabStatusCell from './PartnerMappingTabStatusCell.vue';

type Props = {
  feedId: number;
  sourceId: number;
};

const props = defineProps<Props>();

const router = useRouter();
const { hasScope } = useAuth();
const { iteratively } = useIteratively();
const flashesStore = useFlashesStore();
const partnersStore = usePartnersStore();
const feedsStore = useFeedsStore();

const { partnerOrgs } = storeToRefs(partnersStore);

const feedType = computed(
  () => feedsStore.getFeedById(props.feedId)?.integration?.type,
);
const terminology = computed(() =>
  getPartnerMappingTerminology(feedType.value),
);
const ruleTitle = computed(() => getPartnerMappingRuleTitle(feedType.value));
const ruleSubtitle = computed(
  () =>
    `This mapping rule helps us map your Crossbeam partners to the correct ${terminology.value.objectNamePlural} more accurately.`,
);

const mappings = ref<PartnerCrmMapping[]>([]);
const isLoading = ref(true);
const isSaving = ref(false);
const pendingChanges = ref<
  Map<string, { master_id: string | null; master_name: string | null }>
>(new Map());
const originalMasterIdByPartner = ref<Map<string, string | null>>(new Map());
const partnerMappingTabActionsRef = ref<InstanceType<
  typeof PartnerMappingTabActions
> | null>(null);
const gridApi = ref<GridApi | null>(null);
const searchValue = ref('');
const filterValues = reactive<Record<string, (string | number)[]>>({
  mapping_status: [],
});

const hasReadPermission = computed(() => hasScope('read:integrations'));

const hasAutoMappedAccounts = computed(() => {
  return mappings.value.some((mapping) => mapping.set_by === 'auto_mapping');
});

const hasPendingChanges = computed(() => pendingChanges.value.size > 0);

const filteredMappings = computed(() => {
  let filtered = mappings.value;

  // Apply search filter
  if (searchValue.value) {
    const searchLower = searchValue.value.toLowerCase();
    filtered = filtered.filter((mapping) =>
      (mapping.partner_name || '').toLowerCase().includes(searchLower),
    );
  }

  // Apply mapping status filter
  const statusFilters = filterValues.mapping_status || [];
  if (statusFilters.length > 0) {
    const hasMapped = statusFilters.includes('mapped');
    const hasUnmapped = statusFilters.includes('unmapped');

    // If both filters are selected, show all (no filtering needed)
    if (!(hasMapped && hasUnmapped)) {
      filtered = filtered.filter((mapping) => {
        const isMapped = mapping.master_id !== null;
        return hasMapped ? isMapped : !isMapped;
      });
    }
  }

  return filtered;
});

const usedAccountIds = computed(() => {
  const usedIds = new Set<string>();
  mappings.value.forEach((mapping) => {
    if (mapping.master_id) {
      usedIds.add(mapping.master_id);
    }
  });
  pendingChanges.value.forEach((change) => {
    if (change.master_id) {
      usedIds.add(change.master_id);
    }
  });
  return usedIds;
});

const accountCellParams = computed(() => ({
  feedId: props.feedId,
  onMappingChanged: handleMappingChanged,
  usedAccountIds: usedAccountIds.value,
  sourceName: terminology.value.sourceName,
  objectNamePlural: terminology.value.objectNamePlural,
}));

const columnDefinitions = computed<ColDef[]>(() => [
  {
    headerName: 'Crossbeam Partner',
    field: 'partner_name',
    headerComponent: PartnerMappingTabHeader,
    headerComponentParams: {
      icon: 'logoNoText',
      displayName: 'Crossbeam Partner',
    },
    cellRenderer: PartnerMappingTabPartnerCell,
    flex: 1,
    minWidth: 200,
    suppressMovable: true,
    sortable: false,
  },
  {
    headerName: terminology.value.objectName,
    field: 'master_name',
    headerComponent: PartnerMappingTabHeader,
    headerComponentParams: {
      icon: terminology.value.icon,
      displayName: terminology.value.objectName,
    },
    cellRenderer: PartnerMappingTabAccountCell,
    cellEditor: PartnerMappingTabAccountCell,
    editable: true,
    singleClickEdit: true,
    cellRendererParams: accountCellParams.value,
    cellEditorParams: accountCellParams.value,
    flex: 1,
    minWidth: 250,
    suppressMovable: true,
    sortable: false,
  },
  {
    headerName: 'Status',
    field: 'master_id',
    cellRenderer: PartnerMappingTabStatusCell,
    width: 120,
    minWidth: 120,
    maxWidth: 120,
    suppressMovable: true,
    sortable: false,
  },
]);

function handleNavigateToPartners() {
  router.push({ name: 'partners' });
}

function handleSearchApplied(value: string) {
  searchValue.value = value;
}

function handleFiltersApplied(
  newFilterValues: Record<string, (string | number)[]>,
) {
  filterValues.mapping_status = newFilterValues.mapping_status || [];
}

function handleClearFilters() {
  partnerMappingTabActionsRef.value?.clearAllFilters();
  searchValue.value = '';
  filterValues.mapping_status = [];
}

async function fetchMappings() {
  if (!hasReadPermission.value) {
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  try {
    const { data, error: apiError } = await crossbeamApi.GET(
      '/v0.1/partner-crm-mappings',
    );
    if (apiError) throw apiError;

    const apiMappings = filterMappingsByFeedAndSource(data?.items || []);
    const sortedApiMappings = sortMappingsByPartnerName(apiMappings);
    const missingPartnerMappings = getMissingPartnerMappings(apiMappings);
    mappings.value = [...missingPartnerMappings, ...sortedApiMappings];
    originalMasterIdByPartner.value = new Map(
      mappings.value.map((mapping) => [
        String(mapping.partner_org_id),
        mapping.master_id,
      ]),
    );
  } catch (err) {
    captureException(err);
  } finally {
    isLoading.value = false;
  }
}

/**
 * Tracks local changes to mappings before they are saved.
 */
function handleMappingChanged(
  partnerId: string,
  masterId: string | null,
  masterName: string | null,
) {
  // Track the change
  pendingChanges.value.set(partnerId, {
    master_id: masterId,
    master_name: masterName,
  });

  // Update the local mapping in the array
  const mappingIndex = mappings.value.findIndex(
    (m) => m.partner_org_id === Number(partnerId),
  );
  if (mappingIndex !== -1) {
    mappings.value[mappingIndex] = {
      ...mappings.value[mappingIndex],
      master_id: masterId,
      master_name: masterName,
    } as PartnerCrmMapping;
  }

  // Update row classes to reflect the pending change
  updateRowClassCallback();
}

/**
 * Saves all pending changes to the API at once.
 */
async function handleSaveAll() {
  if (isSaving.value || pendingChanges.value.size === 0) return;

  if (!props.sourceId || isNaN(props.sourceId)) {
    flashesStore.addErrorFlash({
      message: 'Invalid source ID. Cannot save mappings.',
    });
    return;
  }

  isSaving.value = true;
  try {
    const payload = Array.from(pendingChanges.value.entries()).map(
      ([partnerId, change]) => ({
        partner_org_id: Number(partnerId),
        master_id: change.master_id,
        feed_id: props.feedId,
        source_id: props.sourceId,
      }),
    );

    const { error } = await crossbeamApi.PUT('/v0.1/partner-crm-mappings', {
      body: payload,
    });

    if (error) throw error;

    trackManualMatchChanges();
    for (const [partnerId, change] of pendingChanges.value.entries()) {
      originalMasterIdByPartner.value.set(partnerId, change.master_id);
    }

    pendingChanges.value.clear();
    flashesStore.addSuccessFlash({ message: 'Mappings saved successfully' });
    await fetchMappings();
  } catch (err: unknown) {
    captureException(err);
    flashesStore.addErrorFlash({
      message:
        (err as { message?: string })?.message ||
        'Failed to save mappings. Please try again.',
    });
  } finally {
    isSaving.value = false;
  }
}

function trackManualMatchChanges() {
  for (const [partnerId, change] of pendingChanges.value.entries()) {
    const originalMasterId =
      originalMasterIdByPartner.value.get(partnerId) ?? null;
    if (change.master_id === originalMasterId) continue;

    if (change.master_id) {
      iteratively.userSetPartnerMappingManualMatch({
        feed_id: props.feedId,
        source_id: props.sourceId,
        partner_org_id: partnerId,
        crm_account_id: change.master_id,
        crm_type: feedType.value ?? '',
        was_previously_mapped: Boolean(originalMasterId),
      });
    } else {
      iteratively.userClearedPartnerMappingManualMatch({
        feed_id: props.feedId,
        source_id: props.sourceId,
        partner_org_id: partnerId,
      });
    }
  }
}

// Helper functions

function filterMappingsByFeedAndSource(
  mappingsToFilter: PartnerCrmMapping[],
): PartnerCrmMapping[] {
  return mappingsToFilter.filter(
    (m) => m.feed_id === props.feedId && m.source_id === props.sourceId,
  );
}

function sortMappingsByPartnerName(
  mappingsToSort: PartnerCrmMapping[],
): PartnerCrmMapping[] {
  return [...mappingsToSort].sort((a, b) =>
    (a.partner_name || '').localeCompare(b.partner_name || ''),
  );
}

function getMissingPartnerMappings(
  existingMappings: PartnerCrmMapping[],
): PartnerCrmMapping[] {
  const existingPartnerIds = new Set(
    existingMappings.map((m) => String(m.partner_org_id)),
  );

  return partnerOrgs.value
    .filter((partner) => !existingPartnerIds.has(String(partner.id)))
    .map((partner) => ({
      id: 0, // Temporary ID for new mappings
      organization_id: 0, // Will be set by API on save
      partner_org_id: partner.id,
      partner_name: partner.name,
      partner_org_uuid: partner.uuid,
      master_id: null,
      master_name: null,
      master_domain: null,
      feed_id: props.feedId,
      source_id: props.sourceId,
      set_by: 'manual',
    })) as PartnerCrmMapping[];
}

function getRowClass(params: { data: PartnerCrmMapping }) {
  const mapping = params.data;
  if (!mapping) return '';

  const partnerId = String(mapping.partner_org_id);
  return pendingChanges.value.has(partnerId)
    ? 'c-partner-mapping-tab__row--has-pending-change'
    : '';
}

function handleGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
  updateRowClassCallback();
}

function updateRowClassCallback() {
  if (!gridApi.value) return;
  gridApi.value.setGridOption('getRowClass', getRowClass);
  gridApi.value.redrawRows();
}

watch(
  () => pendingChanges.value.size,
  () => {
    updateRowClassCallback();
  },
);

onMounted(async () => {
  if (hasReadPermission.value) {
    iteratively.userViewedPartnerMappingTab({
      feed_id: props.feedId,
      source_id: props.sourceId,
    });
  }
  await partnersStore.readySync;
  fetchMappings();
});

// Expose state to parent modal for footer buttons
defineExpose({
  hasPendingChanges,
  isSaving,
  handleSaveAll,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-partner-mapping-tab {
  @apply flex flex-col;
}

.c-partner-mapping-content {
  @apply flex flex-col gap-16;
}

.c-partner-mapping-tab__title {
  @apply text-neutral-900 text-[1rem] font-bold mb-8;
}

.c-partner-mapping-tab__subtitle {
  @apply text-neutral-text-weak text-base;
}

.c-partner-mapping-tab__rule-box {
  @apply w-[1070px];
}

/* Override AG Grid row backgrounds to white */
.c-partner-mapping-tab__table :deep(.ag-row),
.c-partner-mapping-tab__table :deep(.ag-row-odd),
.c-partner-mapping-tab__table :deep(.ag-row-even),
.c-partner-mapping-tab__table :deep(.ag-row:hover) {
  @apply bg-white;
}

/* Header bottom border */
.c-partner-mapping-tab__table :deep(.ag-header) {
  @apply !border-b !border-neutral-border;
}

/* Row borders */
.c-partner-mapping-tab__table :deep(.ag-row) {
  /* next line isn't working in tailwind for some reason */
  border-bottom: 1px solid theme(colors.neutral.border) !important;
}

.c-partner-mapping-tab__table :deep(.ag-row-last) {
  @apply !border-b-0;
}

/* Highlight rows with pending changes */
.c-partner-mapping-tab__table
  :deep(.ag-row.c-partner-mapping-tab__row--has-pending-change) {
  @apply bg-denim-50/25;
}

/* Remove extra space at bottom when there are few rows */
.c-partner-mapping-tab__table :deep(.ag-center-cols-viewport),
.c-partner-mapping-tab__table :deep(.ag-body-viewport),
.c-partner-mapping-tab__table :deep(.ag-body-viewport-wrapper) {
  min-height: 0 !important;
}
</style>

<style lang="pcss">
@reference "../../../../../css/theme.css";
.c-partner-mapping-tab__icon {
  @apply flex items-center justify-center p-20 rounded-[360px] text-neutral-accent;
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
